import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Toaster } from "sonner";
import { useAuth } from "./auth";
import { useLang } from "./i18n";
import { DiscountPopup } from "./components/DiscountPopup";
import { ChatWidget } from "./components/ChatWidget";

export function Reveal({ children, className = "" }) {
  return <motion.div className={className} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: .16 }} transition={{ duration: .7, ease: [.22, 1, .36, 1] }}>{children}</motion.div>;
}

export function LangSwitch() {
  const { lang, setLang } = useLang();
  return (
    <span className="lang-switch" data-testid="language-switch">
      {["en", "lt"].map(l => (
        <button key={l} type="button" className={lang === l ? "active" : ""} onClick={() => setLang(l)} data-testid={`lang-${l}-button`}>{l.toUpperCase()}</button>
      ))}
    </span>
  );
}

export function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLang();
  const links = [["/", t("nav.home")], ["/services", t("nav.services")], ["/about", t("nav.about")], ["/contact", t("nav.book")]];
  return (
    <header className="nav-wrap">
      <nav className="nav" data-testid="main-navigation">
        <Link to="/" className="brand" data-testid="brand-home-link"><span className="brand-mark">NN</span><span>NN <b>MECHANIC</b></span></Link>
        <button className="menu-btn" data-testid="mobile-menu-button" onClick={() => setOpen(!open)} aria-label="Toggle menu">{open ? <X /> : <Menu />}</button>
        <div className={`nav-links ${open ? "is-open" : ""}`}>
          {links.map(([path, label]) => <Link key={path} to={path} onClick={() => setOpen(false)} className={location.pathname === path ? "active" : ""} data-testid={`nav-${path === "/" ? "home" : path.slice(1)}-link`}>{label}</Link>)}
          {user
            ? <Link to="/account" onClick={() => setOpen(false)} className={location.pathname === "/account" ? "active" : ""} data-testid="nav-account-link">{t("nav.garage")}</Link>
            : <Link to="/login" onClick={() => setOpen(false)} className={location.pathname === "/login" ? "active" : ""} data-testid="nav-signin-link">{t("nav.signin")}</Link>}
          <LangSwitch />
          <Link to="/admin" className="nav-admin" data-testid="nav-admin-link">{t("nav.admin")} <ArrowUpRight size={15} /></Link>
        </div>
      </nav>
    </header>
  );
}

export function Footer() {
  const { t } = useLang();
  return <footer className="footer"><div><div className="brand"><span className="brand-mark">NN</span><span>NN <b>MECHANIC</b></span></div><p>{t("footer.tagline")}</p></div><div className="footer-contact"><a href="tel:+37061358551" data-testid="footer-phone-link">+370 613 58551</a><div className="footer-msg"><a href="https://wa.me/37061358551" target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link">WhatsApp</a><a href="viber://chat?number=%2B37061358551" data-testid="footer-viber-link">Viber</a></div><span>Staneviciaus g. 86B, Vilnius</span></div><span className="footer-code">NNM / 2026</span></footer>;
}

export function Shell({ children }) {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let frame;
    const raf = (time) => { lenis.raf(time); frame = requestAnimationFrame(raf); };
    frame = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(frame); lenis.destroy(); };
  }, []);
  return <><Nav />{children}<Footer /><DiscountPopup /><ChatWidget /><Toaster theme="dark" position="bottom-right" /></>;
}
